import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { ErrorBox } from '../components/DataTable';

/**
 * 경유지 편집.
 *
 * 저장은 **목록 통째로** 보낸다. 서버가 기존 것을 내리고 순서를 0부터 다시 매긴다 —
 * 화면이 순서값을 관리하지 않는다. 빈 줄은 서버가 버리므로 "추가만 하고 안 채운 칸"이 남지 않는다.
 */
export default function ViaEditor({
  disptId,
  readOnly,
  onSaved,
}: {
  disptId: number;
  readOnly: boolean;
  onSaved?: (msg: string) => void;
}) {
  const [items, setItems] = useState<{ viaLoc: string; startHm: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .listVias({ disptId })
      .then((r) => {
        if (cancelled) return;
        setItems(r.rows.map((v) => ({ viaLoc: v.via_loc ?? '', startHm: v.start_hm ?? '' })));
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [disptId]);

  const set = (i: number, patch: Partial<{ viaLoc: string; startHm: string }>) =>
    setItems((cur) => cur.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));

  const move = (i: number, dir: -1 | 1) =>
    setItems((cur) => {
      const j = i + dir;
      if (j < 0 || j >= cur.length) return cur;
      const next = [...cur];
      const a = next[i]!;
      next[i] = next[j]!;
      next[j] = a;
      return next;
    });

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const r = await api.saveVias({ disptId, items });
      onSaved?.(`경유지 ${r.count}곳을 저장했습니다.`);
      setOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  if (loading) return null;

  const filled = items.filter((i) => i.viaLoc.trim() !== '');

  if (!open) {
    return (
      <div className="flex items-center gap-3 text-sm">
        <span className="font-semibold text-ink-soft">경유지 {filled.length}곳</span>
        {filled.length > 0 && (
          <span className="text-ink-soft">
            {filled.map((i) => `${i.viaLoc}${i.startHm ? ` ${i.startHm}` : ''}`).join(' → ')}
          </span>
        )}
        {!readOnly && (
          <button className="btn-ghost py-1 text-xs" onClick={() => setOpen(true)}>
            {filled.length ? '경유지 수정' : '경유지 추가'}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-line bg-canvas p-3">
      <div className="mb-2 flex items-center gap-2">
        <span className="text-sm font-semibold">경유지</span>
        <span className="text-xs text-ink-faint">순서대로 저장됩니다. 빈 칸은 저장되지 않습니다.</span>
        <button className="btn-primary ml-auto py-1 text-xs" onClick={save} disabled={saving}>
          {saving ? '저장 중…' : '저장'}
        </button>
        <button className="btn-ghost py-1 text-xs" onClick={() => setOpen(false)} disabled={saving}>
          닫기
        </button>
      </div>

      {error && <ErrorBox message={error} />}

      <ul className="space-y-2">
        {items.map((it, i) => (
          <li key={i} className="flex items-center gap-2">
            <span className="w-5 text-center text-xs text-ink-faint">{i + 1}</span>
            <input
              className="field flex-1"
              placeholder="경유지"
              value={it.viaLoc}
              onChange={(e) => set(i, { viaLoc: e.target.value })}
            />
            <input
              className="field w-24"
              placeholder="HH:mm"
              value={it.startHm}
              onChange={(e) => set(i, { startHm: e.target.value })}
            />
            <button className="btn-ghost px-2 py-1 text-xs" onClick={() => move(i, -1)} disabled={i === 0}>
              ↑
            </button>
            <button
              className="btn-ghost px-2 py-1 text-xs"
              onClick={() => move(i, 1)}
              disabled={i === items.length - 1}
            >
              ↓
            </button>
            <button
              className="btn-ghost px-2 py-1 text-xs"
              onClick={() => setItems((cur) => cur.filter((_, idx) => idx !== i))}
            >
              삭제
            </button>
          </li>
        ))}
      </ul>

      <button
        className="btn-ghost mt-2 py-1 text-xs"
        onClick={() => setItems((cur) => [...cur, { viaLoc: '', startHm: '' }])}
      >
        + 경유지 추가
      </button>
    </div>
  );
}
